import React, { useState } from 'react';
import { Star, ThumbsUp, Filter } from 'lucide-react';
import { businessInfo, reviews } from '../mockData';

const Reviews = () => {
  const [filterRating, setFilterRating] = useState('all');
  const [sortBy, setSortBy] = useState('recent');

  const filteredReviews = reviews.filter(review => {
    if (filterRating === 'all') return true;
    return review.rating === parseInt(filterRating);
  });

  const sortedReviews = [...filteredReviews].sort((a, b) => {
    if (sortBy === 'recent') {
      return 0; // Keep mock order
    } else if (sortBy === 'highest') {
      return b.rating - a.rating;
    }
    return a.rating - b.rating;
  });

  const ratingCounts = [5, 4, 3, 2, 1].map(rating => ({
    stars: rating,
    count: reviews.filter(r => r.rating === rating).length,
    percentage: (reviews.filter(r => r.rating === rating).length / reviews.length) * 100
  }));

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      {/* Header Section */}
      <section className="py-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Customer Reviews
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              See what our customers say about our phone repair services
            </p>
          </div>

          {/* Rating Summary */}
          <div className="max-w-4xl mx-auto bg-neutral-900 border border-neutral-800 rounded-2xl p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="text-center md:border-r border-neutral-800">
                <p className="text-6xl font-bold text-white mb-2">{businessInfo.rating}</p>
                <div className="flex justify-center mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`w-6 h-6 ${i < Math.floor(businessInfo.rating) ? 'text-white fill-white' : 'text-slate-600'}`}
                    />
                  ))}
                </div>
                <p className="text-gray-400">Based on {businessInfo.reviewCount}+ reviews</p>
              </div>

              <div className="space-y-2">
                {ratingCounts.map(({ stars, count, percentage }) => (
                  <div key={stars} className="flex items-center gap-3">
                    <div className="flex items-center gap-1 w-16">
                      <span className="text-white font-medium">{stars}</span>
                      <Star className="w-4 h-4 text-white fill-white" />
                    </div>
                    <div className="flex-1 h-3 bg-slate-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-white rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-gray-400 text-sm w-12 text-right">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-neutral-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex items-center gap-4">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={filterRating}
                onChange={(e) => setFilterRating(e.target.value)}
                className="px-4 py-2 bg-neutral-900 border border-neutral-800 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white"
              >
                <option value="all">All Ratings</option>
                <option value="5">5 Stars</option>
                <option value="4">4 Stars</option>
                <option value="3">3 Stars</option>
                <option value="2">2 Stars</option>
                <option value="1">1 Star</option>
              </select>
            </div>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 bg-neutral-900 border border-neutral-800 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white"
            >
              <option value="recent">Most Recent</option>
              <option value="highest">Highest Rating</option>
              <option value="lowest">Lowest Rating</option>
            </select>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            {sortedReviews.map((review) => (
              <div 
                key={review.id}
                className="p-6 bg-neutral-900 border border-neutral-800 rounded-xl hover:border-white/50 transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-white">{review.name}</h3>
                      {review.verified && (
                        <span className="px-2 py-0.5 bg-white/10 border border-white/20 rounded text-white text-xs font-medium">
                          Verified
                        </span>
                      )}
                    </div>
                    <p className="text-gray-500 text-sm">{review.date}</p>
                  </div>
                  <div className="flex">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-white fill-white" />
                    ))}
                  </div>
                </div>
                <p className="text-gray-300 leading-relaxed mb-4">
                  {review.text}
                </p>
                <button className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm">
                  <ThumbsUp className="w-4 h-4" />
                  Helpful
                </button>
              </div>
            ))}
          </div>

          {sortedReviews.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">No reviews match your filter criteria.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 lg:p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Experience Our Service Yourself
            </h2>
            <p className="text-gray-300 mb-8 text-lg">
              Join hundreds of satisfied customers in El Paso who trust us with their phone repairs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`tel:${businessInfo.phone}`}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white hover:bg-gray-100 text-black font-bold text-white font-semibold rounded-xl transition-all shadow-lg shadow-white/30 hover:shadow-white/50 hover:scale-105"
              >
                Call Now: {businessInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
